import compact from 'lodash/compact'
import noop from 'lodash/noop'
import orderBy from 'lodash/orderBy'
import { getImgProps } from 'next/dist/shared/lib/get-img-props'
import { imageConfigDefault } from 'next/dist/shared/lib/image-config'
import Head from 'next/head'
import NextImage, { ImageProps } from 'next/image'
import NextLegacyImage, { ImageProps as ImageLegacyProps } from 'next/legacy/image'
import React, { CSSProperties, FC, useMemo, useRef } from 'react'

import { ImagePins } from '@Components/Image/Pins'
import { Image as ImageData, ImagePin } from '@Globals/Image'

import styles from './Image.module.scss'
import {
  assetCDNLoader,
  contentstackLoader,
  generateProductLinkingDataImage,
  generateSrcSet,
  imageLoader,
} from './loader'

// the URL builders live in ./loader, which holds no React, so a server component can import them on their own
export { assetCDNLoader, contentstackLoader, generateProductLinkingDataImage, imageLoader }

interface ImageV2Props extends ImageLegacyProps {
  cropRatio?: string
}

export const ImageV2: FC<ImageV2Props> = (props) => {
  const {
    cropRatio,
    objectFit,
    objectPosition,
    src,
    ...rest
  } = props

  if (!src) {
    console.error('invalid src for ImageV2')
    return null
  }

  return (
    <NextLegacyImage
      loader={params => imageLoader({
        ...params,
        cropRatio,
        objectFit,
        objectPosition,
      })}
      objectFit={objectFit}
      src={src}
      {...rest}
    />
  )
}

interface ImageV3Props extends ImageProps {
  className?: string
  cropRatio?: string
  // when set, the image renders with exactly these srcset candidate widths
  // instead of the global config's deviceSizes+imageSizes expansion — own
  // the list at the call site, next to the sizes value it corresponds to
  deviceSizes?: number[]
  overrideQuality?: number | string
}

// with deviceSizes set, the img props come from next/image's own getImgProps
// (fill styles, loading/decoding defaults and candidate selection included),
// computed against the given candidate list (imageSizes is emptied so its
// small entries aren't folded back in), and `priority` renders the same head
// preload the client component would — same loader and URL params either
// way, so the prop is the only difference between the two paths
export const ImageV3: FC<ImageV3Props> = ({
  className,
  cropRatio,
  deviceSizes,
  onLoad,
  overrideQuality,
  priority,
  src,
  style,
  ...rest
}) => {
  // deviceSizes path only: an SSR/cached image can finish loading before
  // hydration, in which case the browser's load event never reaches React —
  // the ref below detects that (like next/image's handleLoading does) and
  // fires the handler after decode, guarded to a single invocation
  const loadFired = useRef(false)

  if (!src) {
    console.error('invalid src for ImageV3')
    return null
  }

  const loader = ({ quality, src: loaderSrc, width }: { quality?: number, src: string, width: number }) => imageLoader({
    cropRatio,
    objectFit: style?.objectFit,
    objectPosition: style?.objectPosition,
    quality: overrideQuality || quality,
    src: loaderSrc,
    width,
  })

  if (!deviceSizes) {
    return (
      <NextImage
        className={className}
        loader={loader}
        onLoad={onLoad}
        priority={priority}
        src={src}
        style={style}
        {...rest}
      />
    )
  }

  const { props } = getImgProps(
    {
      ...rest,
      className,
      loader,
      priority,
      src,
      style,
    },
    {
      defaultLoader: loader as Parameters<typeof getImgProps>[1]['defaultLoader'],
      imgConf: { ...imageConfigDefault, deviceSizes, imageSizes: [] },
    },
  )

  return (
    <>
      {priority && (
        <Head>
          {/* mirrors next/image's ImagePreload for the pages router; href
              is deliberately omitted — browsers without imagesrcset support
              would otherwise preload the wrong candidate */}
          <link
            key={`__nimg-${props.src}${props.srcSet}${props.sizes}`}
            as="image"
            crossOrigin={props.crossOrigin}
            fetchPriority={props.fetchPriority}
            imageSizes={props.sizes}
            imageSrcSet={props.srcSet}
            referrerPolicy={props.referrerPolicy}
            rel="preload"
          />
        </Head>
      )}
      {/* getImgProps consumes onLoad (the next/image client component wires
          it through its own ref handling), so it's reattached here, with the
          ref reproducing that handling's already-complete check. Lifecycle
          props beyond onLoad (onLoadingComplete, placeholder) are not
          supported on the deviceSizes path. */}
      {/* eslint-disable-next-line jsx-a11y/alt-text */}
      <img
        {...props}
        onLoad={(event) => {
          loadFired.current = true
          onLoad?.(event)
        }}
        ref={(img) => {
          if (!img || !onLoad || loadFired.current || !img.complete) {
            return
          }

          loadFired.current = true
          // like next/image: wait for decode (ignoring its failure) and only
          // fire for an image still in the document
          img.decode().catch(noop).then(() => {
            if (img.isConnected) {
              onLoad({ target: img } as unknown as React.SyntheticEvent<HTMLImageElement>)
            }
          })
        }}
      />
    </>
  )
}

type ImageV4Props = Omit<React.JSX.IntrinsicElements['img'], 'src' | 'width'> & {
  quality?: number
  src: string
  width: number
}

export const ImageV4: FC<ImageV4Props> = ({
  alt,
  onLoad = noop,
  quality,
  src,
  style,
  width,
  ...rest
}) => (
  <img
    alt={alt}
    onLoad={onLoad}
    src={imageLoader({
      quality,
      src,
      width: width * 2,
      objectFit: style?.objectFit,
      objectPosition: style?.objectPosition,
    })}
    srcSet={generateSrcSet({
      quality,
      src,
      width,
      objectFit: style?.objectFit,
      objectPosition: style?.objectPosition,
    })}
    style={style}
    {...rest}
  />
)

type IMGStyleProps = NonNullable<React.JSX.IntrinsicElements['img']['style']>

/** number is converted to pixels (px), and string is parsed as viewport width (vw) */
type PixelsOrViewportWidth = number | string

type Breakpoint = [
  /** min-width breakpoint */
  breakpoint: number,
  /** max container width before next breakpoint */
  width: PixelsOrViewportWidth,
  /** max container height */
  height?: PixelsOrViewportWidth,
]

export type PictureV2Props = Omit<React.JSX.IntrinsicElements['img'], 'priority'> & {
  breakpoints: Breakpoint[]
  /** Only applies to desktop */
  cropOnMobile?: boolean
  cropRatio?: string
  desktop: ImageData
  mobile?: ImageData
  objectFit?: IMGStyleProps['objectFit']
  objectPosition?: IMGStyleProps['objectPosition']
  priority?: boolean
}

const calculateBreakpointWidth = (breakpoint: number, width: PixelsOrViewportWidth): number => {
  if (typeof width === 'number') {
    return width
  }

  return (breakpoint / 100) * parseInt(width, 10)
}

export const PictureV2: FC<PictureV2Props> = ({
  alt = '',
  breakpoints,
  cropOnMobile,
  cropRatio,
  desktop,
  mobile,
  objectFit,
  objectPosition,
  priority = false,
  ...imgProps
}) => {
  const breakpointsSorted = useMemo(() => orderBy(breakpoints, [0], ['desc']), [breakpoints])

  const sources = useMemo(() => compact(breakpointsSorted.map(([breakpoint], index) => {
    const next = breakpointsSorted[index - 1]
    if (!next) {
      return undefined
    }
    const isDesktop = breakpoint >= 768
    const [nextBreakpoint, nextWidth] = next
    const width = calculateBreakpointWidth(nextBreakpoint, nextWidth)
    let src = desktop.url
    if (!isDesktop && mobile) {
      src = mobile.url
    }
    const srcSet = generateSrcSet({
      cropRatio: isDesktop || cropOnMobile ? cropRatio : undefined,
      objectFit,
      objectPosition,
      src,
      width,
    })
    return ({ breakpoint, srcSet })
  })), [breakpointsSorted, cropOnMobile, cropRatio, desktop.url, mobile, objectFit, objectPosition])

  return (
    <span
      className={styles.image}
      style={{
        '--desktopHeight': `${desktop.dimensions.height}`,
        '--desktopWidth': `${desktop.dimensions.width}`,
        '--mobileHeight': mobile?.dimensions.height ? `${mobile?.dimensions.height}` : `${desktop.dimensions.height}`,
        '--mobileWidth': mobile?.dimensions.width ? `${mobile?.dimensions.width}` : `${desktop.dimensions.width}`,
      } as CSSProperties}
    >
      <span className={styles.padder} />
      <picture>
        {sources.map(({
          breakpoint,
          srcSet,
        }) => (
          <source
            key={breakpoint}
            media={`(min-width: ${breakpoint}px)`}
            srcSet={srcSet}
          />
        ))}
        <img
          alt={alt}
          fetchPriority={priority ? 'high' : 'auto'}
          srcSet={generateSrcSet({
            src: mobile?.url || desktop.url,
            width: 320,
          })}
          style={{
            objectFit,
            objectPosition,
          } as CSSProperties}
          {...imgProps}
        />
      </picture>
      <ImagePins desktopPins={desktop?.pins} mobilePins={mobile?.pins} />
    </span>
  )
}

function calculateCenterStyle(image: ImageData): React.CSSProperties {
  if (image.dimensions.width === 0 || image.dimensions.height === 0) {
    return {
      height: 'auto',
      width: 'auto',
    }
  }

  if (image.dimensions.width === image.dimensions.height) {
    return {
      aspectRatio: 1,
      minHeight: '100%',
      minWidth: '100%',
    }
  }

  const aspectRatio = image.dimensions.width / image.dimensions.height
  const height = aspectRatio > 1 ? '100%' : 'auto'
  const width = aspectRatio < 1 ? '100%' : 'auto'
  const minHeight = aspectRatio < 1 ? '100%' : undefined
  const minWidth = aspectRatio > 1 ? '100%' : undefined

  return {
    aspectRatio,
    height,
    minHeight,
    minWidth,
    width,
  }
}

export const ImageWithPins = ({
  image,
  loading,
  pins,
  priority,
  sizes,
}: {
  image: ImageData
  loading?: ImageProps['loading']
  pins?: ImagePin[] | null
  priority?: ImageProps['priority']
  sizes?: string
}) => {
  const items = pins || image?.pins || []

  return (
    <div className={styles.imageWithPins}>
      <div className={styles.center} style={calculateCenterStyle(image)}>
        <ImageV3
          alt=""
          height={image?.dimensions?.height || 0}
          loading={loading}
          priority={priority}
          sizes={sizes}
          src={image?.url || ''}
          width={image?.dimensions?.width || 0}
        />
        {items && items.length !== 0 && <ImagePins pins={items} />}
      </div>
    </div>
  )
}
